import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { DashboardService } from "../../../services/dashboard.service";
import { Card } from "../../../models/card.model";
import { UserService } from "../../../services/user.service";
import { User } from "../../../models/user.model";
import { forkJoin } from "rxjs";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { AddUserModalComponent } from "../../../shared/add-user-modal/add-user-modal.component";
import { AssignedDonorsModalComponent } from "../../../shared/assigned-donors-modal/assigned-donors-modal.component";
import { UserProfileModalComponent } from "../../../shared/user-profile-modal/user-profile-modal.component";

@Component({
  selector: "app-user-overview",
  templateUrl: "./user-overview.component.html",
  styleUrl: "./user-overview.component.scss",
})
export class UserOverviewComponent implements OnInit {
  loading = false;
  donors: any[] = [];
  users: User[] = [];
  filteredUsers: User[] = [];
  searchQuery = "";
  adminCards: Card[] = [];
  stats = {
    totalApplicants: 0,
    totalApprovedCandidates: 0,
    inReferral: 0,
    underEvaluation: 0,
    awaitingMRB: 0,
  };

  sortColumn = "";
  sortDirection: "asc" | "desc" = "asc";
  readonly pageSizeOptions = [5, 10, 15, 25, 100, 0];
  private readonly pageSizeStorageKey = "userOverviewPageSize";
  pageSize = 5;
  currentPage = 1;
  totalPages = 1;

  get paginatedUsers(): User[] {
    const pageSize = this.effectivePageSize;
    const start = (this.currentPage - 1) * pageSize;
    return this.filteredUsers.slice(start, start + pageSize);
  }

  get effectivePageSize(): number {
    return this.pageSize === 0 ? this.filteredUsers.length || 1 : this.pageSize;
  }

  private get isMobilePagination(): boolean {
    return typeof window !== "undefined" && window.innerWidth <= 767;
  }

  get paginationItems(): Array<number | string> {
    if (this.isMobilePagination) {
      return [];
    }

    if (this.totalPages <= 5) {
      return Array.from({ length: this.totalPages }, (_, index) => index + 1);
    }

    if (this.currentPage <= 3) {
      return [1, 2, 3, "ellipsis", this.totalPages];
    }

    if (this.currentPage >= this.totalPages - 2) {
      return [
        1,
        "ellipsis",
        this.totalPages - 2,
        this.totalPages - 1,
        this.totalPages,
      ];
    }

    return [1, "ellipsis", this.currentPage, "ellipsis", this.totalPages];
  }

  get pageRangeStart(): number {
    if (this.filteredUsers.length === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageRangeEnd(): number {
    return Math.min(
      this.currentPage * this.effectivePageSize,
      this.filteredUsers.length
    );
  }

  setPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize = pageSize;
    this.cachePageSize(pageSize);
    this.currentPage = 1;
    this.updatePagination();
  }

  updatePagination() {
    this.totalPages =
      Math.ceil(this.filteredUsers.length / this.effectivePageSize) || 1;
    if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;
    if (this.currentPage < 1) this.currentPage = 1;
  }

  constructor(
    private dashboardService: DashboardService,
    private userService: UserService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.restorePageSize();
    this.loadData();
  }

  private restorePageSize(): void {
    if (typeof window === "undefined") {
      return;
    }

    const cachedPageSize = Number(
      window.localStorage.getItem(this.pageSizeStorageKey)
    );

    if (this.pageSizeOptions.includes(cachedPageSize)) {
      this.pageSize = cachedPageSize;
    }
  }

  private cachePageSize(pageSize: number): void {
    if (typeof window === "undefined") {
      return;
    }

    if (this.pageSizeOptions.includes(pageSize)) {
      window.localStorage.setItem(this.pageSizeStorageKey, String(pageSize));
    }
  }

  loadData() {
    this.loading = true;

    forkJoin({
      donors: this.dashboardService.getAllDonors(),
      users: this.userService.getHospitalAdminUsers(),
    }).subscribe({
      next: ({ donors, users }) => {
        this.donors = donors;
        this.updateStats(); // Update donor-related stats

        // Sort users by createdAt (newest first)
        this.users = users.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        const donorAssignmentMap: Record<
          string,
          {
            total: number;
            inReferral: number;
            underEval: number;
            awaitingMRB: number;
            assignedDonors: string[];
          }
        > = {};

        donors.forEach((donor) => {
          if (donor.assignedTo) {
            if (!donorAssignmentMap[donor.assignedTo]) {
              donorAssignmentMap[donor.assignedTo] = {
                total: 0,
                inReferral: 0,
                underEval: 0,
                awaitingMRB: 0,
                assignedDonors: [],
              };
            }

            donorAssignmentMap[donor.assignedTo].total++;
            donorAssignmentMap[donor.assignedTo].assignedDonors.push(
              donor.email
            );

            if (donor.dashboardStatus === "In Referral") {
              donorAssignmentMap[donor.assignedTo].inReferral++;
            } else if (donor.dashboardStatus === "Under Evaluation") {
              donorAssignmentMap[donor.assignedTo].underEval++;
            } else if (donor.dashboardStatus === "Awaiting MRB") {
              donorAssignmentMap[donor.assignedTo].awaitingMRB++;
            }
          }
        });

        // Enhance users with donor count
        this.users = users.map((user) => ({
          ...user,
          donorCount: donorAssignmentMap[user.email]?.total || 0,
          inReferralCount: donorAssignmentMap[user.email]?.inReferral || 0,
          underEvalCount: donorAssignmentMap[user.email]?.underEval || 0,
          awaitingMRBCount: donorAssignmentMap[user.email]?.awaitingMRB || 0,
          assignedonors: donorAssignmentMap[user.email]?.assignedDonors || [],
        }));

        this.filteredUsers = [...this.users];
        this.updatePagination();
        this.currentPage = 1;
        this.loading = false;
      },
      error: (error) => {
        console.error("Error loading data:", error);
        this.loading = false;
      },
    });
  }

  filterUsers(): void {
    this.filteredUsers = this.users.filter(
      (user) =>
        `${user.firstName} ${user.lastName}`
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        user.firstName.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        user.lastName.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        user.userRole.toLowerCase().includes(this.searchQuery.toLowerCase())
    );
    this.updatePagination();
    this.currentPage = 1;
  }

  sortBy(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc"; // Toggle
    } else {
      this.sortColumn = column;
      this.sortDirection = "asc";
    }

    this.filteredUsers.sort((a, b) => {
      let valA = a[column as keyof User] ?? "";
      let valB = b[column as keyof User] ?? "";

      if (typeof valA === "string" && typeof valB === "string") {
        valA = valA.toLowerCase();
        valB = valB.toLowerCase();
      }

      return this.sortDirection === "asc"
        ? valA > valB
          ? 1
          : -1
        : valA < valB
          ? 1
          : -1;
    });
    this.updatePagination();
    this.currentPage = 1;
  }

  getSortIcon(column: string): string {
    if (this.sortColumn === column) {
      return this.sortDirection === "asc" ? "bi-arrow-up" : "bi-arrow-down";
    }
    return ""; // No icon if not sorted
  }

  updateStats() {
    this.stats.totalApplicants = this.donors.length;
    this.stats.totalApprovedCandidates = this.donors.filter(
      (d) => d.dashboardStatus === "Approved"
    ).length;
    this.stats.inReferral = this.donors.filter(
      (d) => d.dashboardStatus === "In Referral"
    ).length;
    this.stats.underEvaluation = this.donors.filter(
      (d) => d.dashboardStatus === "Under Evaluation"
    ).length;
    this.stats.awaitingMRB = this.donors.filter(
      (d) => d.dashboardStatus === "Awaiting MRB"
    ).length;

    this.adminCards = [
      {
        id: "totalApplicants",
        title: "Total Donors & Donor Candidates",
        filterId: "",
        icon: "bi-people",
        value: this.stats.totalApplicants,
      },
      {
        id: "totalApprovedCandidates",
        title: "Total Donors",
        filterId: "",
        icon: "bi-person-check",
        value: this.stats.totalApprovedCandidates,
      },
      {
        id: "inReferral",
        title: "Donor Candidates In Referral",
        filterId: "In Referral",
        icon: "bi-clipboard",
        value: this.stats.inReferral,
      },
      {
        id: "underEvaluation",
        title: "Donor Candidates Under Evaluation",
        filterId: "Under Evaluation",
        icon: "bi-arrow-repeat",
        value: this.stats.underEvaluation,
      },
      {
        id: "awaitingMRB",
        title: "Donor Candidates Awaiting Review",
        filterId: "Awaiting MRB",
        icon: "bi-clock",
        value: this.stats.awaitingMRB,
      },
    ];
  }

  openAddUserModal() {
    const modalRef = this.modalService.open(AddUserModalComponent, {
      centered: true,
      backdrop: "static",
      size: "lg",
    });

    modalRef.componentInstance.closeSuccess.subscribe(() => {
      this.loadData();
    });
  }

  openAssignedDonorsModal(user: any) {
    const modalRef = this.modalService.open(AssignedDonorsModalComponent, {
      centered: true,
      backdrop: "static",
    });

    modalRef.componentInstance.user = user;
    modalRef.componentInstance.donors = this.donors;
  }

  openUserProfile(user: any) {
    const modalRef = this.modalService.open(UserProfileModalComponent, {
      centered: true,
      backdrop: "static",
    });

    modalRef.componentInstance.user = user;

    modalRef.result
      .then((result: any) => {
        if (result) {
          this.loadData();
        }
      })
      .catch(() => {
        /* dismissed – no action */
      });
  }
}
